import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export type PurchasingItemType = 'product' | 'raw_item' | 'variant';

export interface PurchasingRecommendation {
  item_type: PurchasingItemType;
  item_id: string;
  product_id: string | null;
  raw_item_id: string | null;
  variant_id: string | null;
  location_id: string;
  location_name: string;
  supplier_id: string | null;
  supplier_name: string | null;
  item_name: string;
  sku: string | null;
  unit_of_measure: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  in_transit: number;
  on_order: number;
  net_available: number;
  daily_demand: number;
  projected_demand_14_days: number;
  minimum_stock: number;
  maximum_stock: number;
  proposed_minimum_stock: number;
  proposed_maximum_stock: number;
  order_increment: number;
  recommended_quantity: number;
  estimated_unit_cost: number;
  estimated_cost: number;
  lead_time_days: number;
  last_received_at: string | null;
  typical_reorder_days: number | null;
  confidence: 'low' | 'medium' | 'high';
  priority: 'urgent' | 'high' | 'overstock' | 'normal';
  policy_status: 'approved' | 'proposed' | 'missing';
  reason: string;
  evidence: Record<string, unknown>;
}

export interface PurchasingFilters {
  locationId?: string;
  supplierId?: string;
}

export interface PurchasingSpendInsight {
  supplier_id: string | null;
  supplier_name: string;
  spend_last_30_days: number;
  spend_previous_30_days: number;
  spend_change: number;
  change_percent: number | null;
}

interface PolicyInput {
  recommendation: PurchasingRecommendation;
  minimumStock: number;
  maximumStock: number;
  orderIncrement: number;
  approved: boolean;
}

const numeric = (value: unknown) => Number(value || 0);

function normalizeRecommendation(row: Record<string, unknown>): PurchasingRecommendation {
  return {
    ...row,
    item_type: row.item_type as PurchasingItemType,
    item_id: String(row.item_id),
    product_id: row.product_id as string | null,
    raw_item_id: row.raw_item_id as string | null,
    variant_id: row.variant_id as string | null,
    location_id: String(row.location_id),
    location_name: String(row.location_name || 'Unknown location'),
    supplier_id: row.supplier_id as string | null,
    supplier_name: row.supplier_name as string | null,
    item_name: String(row.item_name || 'Unknown item'),
    sku: row.sku as string | null,
    unit_of_measure: String(row.unit_of_measure || 'each'),
    quantity_on_hand: numeric(row.quantity_on_hand),
    quantity_reserved: numeric(row.quantity_reserved),
    in_transit: numeric(row.in_transit),
    on_order: numeric(row.on_order),
    net_available: numeric(row.net_available),
    daily_demand: numeric(row.daily_demand),
    projected_demand_14_days: numeric(row.projected_demand_14_days),
    minimum_stock: numeric(row.minimum_stock),
    maximum_stock: numeric(row.maximum_stock),
    proposed_minimum_stock: numeric(row.proposed_minimum_stock),
    proposed_maximum_stock: numeric(row.proposed_maximum_stock),
    order_increment: numeric(row.order_increment) || 1,
    recommended_quantity: numeric(row.recommended_quantity),
    estimated_unit_cost: numeric(row.estimated_unit_cost),
    estimated_cost: numeric(row.estimated_cost),
    lead_time_days: numeric(row.lead_time_days),
    last_received_at: row.last_received_at as string | null,
    typical_reorder_days: row.typical_reorder_days === null ? null : numeric(row.typical_reorder_days),
    confidence: row.confidence as PurchasingRecommendation['confidence'],
    priority: row.priority as PurchasingRecommendation['priority'],
    policy_status: row.policy_status as PurchasingRecommendation['policy_status'],
    reason: String(row.reason || ''),
    evidence: (row.evidence || {}) as Record<string, unknown>,
  };
}

export function usePurchasingManager(filters: PurchasingFilters = {}) {
  const { organization, user } = useAuth();
  const queryClient = useQueryClient();

  const recommendationsQuery = useQuery({
    queryKey: ['purchasing-manager-recommendations', organization?.id, filters],
    queryFn: async () => {
      if (!organization?.id) return [] as PurchasingRecommendation[];
      const { data, error } = await supabase.rpc('get_purchasing_manager_recommendations', {
        p_organization_id: organization.id,
        p_location_id: filters.locationId || null,
        p_supplier_id: filters.supplierId || null,
      });
      if (error) throw error;
      return ((data || []) as Record<string, unknown>[]).map(normalizeRecommendation);
    },
    enabled: !!organization?.id,
  });

  const spendInsightsQuery = useQuery({
    queryKey: ['purchasing-manager-spend-insights', organization?.id, filters],
    queryFn: async () => {
      if (!organization?.id) return [] as PurchasingSpendInsight[];
      const { data, error } = await supabase.rpc('get_purchasing_spend_insights', {
        p_organization_id: organization.id,
        p_location_id: filters.locationId || null,
        p_supplier_id: filters.supplierId || null,
      });
      if (error) throw error;
      return ((data || []) as Record<string, unknown>[]).map((row) => ({
        supplier_id: row.supplier_id as string | null,
        supplier_name: String(row.supplier_name || 'Unassigned supplier'),
        spend_last_30_days: numeric(row.spend_last_30_days),
        spend_previous_30_days: numeric(row.spend_previous_30_days),
        spend_change: numeric(row.spend_change),
        change_percent: row.change_percent === null ? null : numeric(row.change_percent),
      }));
    },
    enabled: !!organization?.id,
  });

  const savePolicy = useMutation({
    mutationFn: async ({ recommendation, minimumStock, maximumStock, orderIncrement, approved }: PolicyInput) => {
      if (!organization?.id || !user?.id) throw new Error('Organization context is required.');
      const itemColumn = recommendation.item_type === 'raw_item'
        ? 'raw_item_id'
        : recommendation.item_type === 'variant'
          ? 'variant_id'
          : 'product_id';
      const itemId = recommendation.item_type === 'raw_item'
        ? recommendation.raw_item_id
        : recommendation.item_type === 'variant'
          ? recommendation.variant_id
          : recommendation.product_id;
      if (!itemId) throw new Error('This inventory item is missing its catalog reference.');

      const { data: existing, error: existingError } = await supabase
        .from('purchasing_stock_policies')
        .select('id')
        .eq('location_id', recommendation.location_id)
        .eq(itemColumn, itemId)
        .maybeSingle();
      if (existingError) throw existingError;

      const payload = {
        organization_id: organization.id,
        location_id: recommendation.location_id,
        product_id: recommendation.item_type === 'product' ? itemId : null,
        raw_item_id: recommendation.item_type === 'raw_item' ? itemId : null,
        variant_id: recommendation.item_type === 'variant' ? itemId : null,
        minimum_stock: approved ? minimumStock : null,
        maximum_stock: approved ? maximumStock : null,
        proposed_minimum_stock: minimumStock,
        proposed_maximum_stock: maximumStock,
        order_increment: orderIncrement,
        is_approved: approved,
        approved_by: approved ? user.id : null,
        approved_at: approved ? new Date().toISOString() : null,
      };
      const request = existing?.id
        ? supabase.from('purchasing_stock_policies').update(payload).eq('id', existing.id)
        : supabase.from('purchasing_stock_policies').insert(payload);
      const { error } = await request;
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-manager-recommendations'] }),
  });

  const saveSupplierSchedule = useMutation({
    mutationFn: async (input: { supplierId: string; leadTimeDays: number; orderDays: number[]; orderCutoff?: string | null }) => {
      const { error } = await supabase.from('purchasing_supplier_settings').upsert({
        supplier_id: input.supplierId,
        lead_time_days: input.leadTimeDays,
        order_days: input.orderDays,
        order_cutoff: input.orderCutoff || null,
        updated_by: user?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-manager-recommendations'] }),
  });

  const createDraftPurchaseOrders = useMutation({
    mutationFn: async (selected: PurchasingRecommendation[]) => {
      if (selected.length === 0) throw new Error('Select at least one recommendation.');
      const unsupported = selected.filter((item) => !item.supplier_id || item.recommended_quantity <= 0);
      if (unsupported.length > 0) throw new Error('Every selected recommendation needs a supplier and a positive recommended quantity.');

      const groups = new Map<string, PurchasingRecommendation[]>();
      selected.forEach((item) => {
        const key = `${item.location_id}:${item.supplier_id}`;
        groups.set(key, [...(groups.get(key) || []), item]);
      });
      const createdOrderIds: string[] = [];
      for (const items of groups.values()) {
        const first = items[0];
        const subtotal = items.reduce((sum, item) => sum + item.estimated_cost, 0);
        const orderNumber = `PM-${new Date().toISOString().slice(0, 10).replaceAll('-', '')}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
        const { data: order, error: orderError } = await supabase
          .from('purchase_orders')
          .insert({
            location_id: first.location_id,
            supplier_id: first.supplier_id,
            order_number: orderNumber,
            status: 'draft',
            subtotal,
            tax: 0,
            total: subtotal,
            notes: 'Draft created by Purchasing Manager. Review forecast evidence before placing this order.',
          })
          .select('id')
          .single();
        if (orderError || !order) throw orderError || new Error('Unable to create purchase order.');
        const { error: itemsError } = await supabase.from('purchase_order_items').insert(items.map((item) => ({
          purchase_order_id: order.id,
          product_id: item.product_id,
          raw_item_id: item.raw_item_id,
          description: item.item_type === 'variant' ? `Variant: ${item.item_name}` : item.item_name,
          quantity_ordered: item.recommended_quantity,
          quantity_received: 0,
          unit_cost: item.estimated_unit_cost,
        })));
        if (itemsError) throw itemsError;
        createdOrderIds.push(order.id);
      }
      return createdOrderIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchasing-manager-recommendations'] });
    },
  });

  const askAssistant = useMutation({
    mutationFn: async (input: { question: string; locationId?: string; supplierId?: string }) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.access_token) throw new Error('Your session has expired. Please sign in again before using Operations Assistant.');
      const { data, error } = await supabase.functions.invoke('purchasing-assistant', {
        body: { question: input.question, locationId: input.locationId || null, supplierId: input.supplierId || null },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as { answer: string; provider: string; asOf: string; evidence: Array<Record<string, unknown>> };
    },
  });

  const captureSnapshot = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error('Organization context is required.');
      const { data, error } = await supabase.rpc('capture_purchasing_forecast_snapshot', {
        p_organization_id: organization.id,
        p_location_id: filters.locationId || null,
      });
      if (error) throw error;
      return data as string;
    },
  });

  return {
    recommendations: recommendationsQuery.data || [],
    spendInsights: spendInsightsQuery.data || [],
    isLoading: recommendationsQuery.isLoading,
    error: recommendationsQuery.error,
    refetch: recommendationsQuery.refetch,
    savePolicy,
    saveSupplierSchedule,
    createDraftPurchaseOrders,
    askAssistant,
    captureSnapshot,
  };
}
